import type { ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CodeBlock,
  CodeBlockBody,
  CodeBlockCopyButton,
  CodeBlockFilename,
  CodeBlockHead,
  Snippet,
  Tabs,
} from "@diametral/design-system/react";

import { Page } from "../components/Page";
import { config } from "../lib/config";
import { Trans, useTranslation } from "../lib/i18n";

/**
 * Every client registers itself with ada's Keycloak (dynamic client registration, RFC 7591): the
 * person pastes the server URL, signs in with Microsoft, and that's it — no client ID, no secret.
 * Redirect hosts accepted for registration: `MCP_DCR_TRUSTED_HOSTS` in `ada/jobs/realm_sync.py`.
 */
export const CLIENTS = ["claude", "claude-code", "chatgpt", "le-chat", "vscode", "gemini-cli"] as const;
export type Client = (typeof CLIENTS)[number];

export function claudeCodeCommand(mcpUrl: string): string {
  return `claude mcp add --scope user --transport http ada ${mcpUrl}`;
}

export function vscodeConfig(mcpUrl: string): string {
  return JSON.stringify({ servers: { ada: { type: "http", url: mcpUrl } } }, null, 2);
}

export function geminiConfig(mcpUrl: string): string {
  return JSON.stringify({ mcpServers: { ada: { httpUrl: mcpUrl } } }, null, 2);
}

function Code({ filename, code }: { filename: string; code: string }) {
  return (
    <CodeBlock>
      <CodeBlockHead>
        <CodeBlockFilename>{filename}</CodeBlockFilename>
        <CodeBlockCopyButton value={code} />
      </CodeBlockHead>
      <CodeBlockBody code={code} />
    </CodeBlock>
  );
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className="ada-steps">{children}</ol>;
}

/** A menu path in the third-party tool, e.g. Settings › Connectors. */
function Menu({ children }: { children?: ReactNode }) {
  return <strong>{children}</strong>;
}

function ClientGuide({ client, mcpUrl }: { client: Client; mcpUrl: string }) {
  const { t } = useTranslation();
  const url = <Snippet value={mcpUrl} />;
  switch (client) {
    case "claude":
      return (
        <div className="ada-stack">
          <p className="ada-muted">{t("claude.ai on the web, Claude Desktop and the mobile apps: the connector is added once and follows your account.")}</p>
          <Steps>
            <li>
              <Trans i18nKey="Open <0>Settings › Connectors</0>, then <0>Add custom connector</0>." components={[<Menu />]} />
            </li>
            <li>
              {t("Name it “ada” and paste the server URL — nothing else, the advanced settings stay empty:")}
              {url}
            </li>
            <li>
              <Trans i18nKey="Click <0>Connect</0> and sign in with your Diametral Microsoft account." components={[<Menu />]} />
            </li>
            <li>
              <Trans i18nKey="In a conversation, turn ada on from the <0>+</0> menu › <0>Connectors</0>." components={[<Menu />]} />
            </li>
          </Steps>
          <Callout type="info">
            {t("Team and Enterprise plans: an organisation owner first adds the connector in the organisation settings; each person then clicks Connect.")}
          </Callout>
        </div>
      );
    case "claude-code":
      return (
        <div className="ada-stack">
          <p className="ada-muted">{t("In a terminal, once:")}</p>
          <Snippet value={claudeCodeCommand(mcpUrl)} />
          <Steps>
            <li>
              <Trans i18nKey="Then, in Claude Code, type <0>/mcp</0>, pick “ada” and choose <1>Authenticate</1>." components={[<code className="ada-mono" />, <Menu />]} />
            </li>
            <li>{t("Your browser opens: sign in with your Diametral Microsoft account.")}</li>
          </Steps>
        </div>
      );
    case "chatgpt":
      return (
        <div className="ada-stack">
          <Steps>
            <li>
              <Trans i18nKey="Turn on developer mode: <0>Settings › Apps & Connectors › Advanced settings › Developer mode</0>." components={[<Menu />]} />
            </li>
            <li>
              <Trans i18nKey="Back in <0>Apps & Connectors</0>, click <0>Create</0>: name “ada”, authentication <0>OAuth</0>, and the server URL:" components={[<Menu />]} />
              {url}
            </li>
            <li>{t("Tick that you trust the application, create it, then sign in with your Diametral Microsoft account.")}</li>
            <li>
              <Trans i18nKey="In a conversation: <0>+ › Developer mode</0>, then select ada." components={[<Menu />]} />
            </li>
          </Steps>
          <Callout type="info">
            {t("Developer mode depends on the ChatGPT plan; on Business and Enterprise plans an administrator has to allow it.")}
          </Callout>
        </div>
      );
    case "le-chat":
      return (
        <div className="ada-stack">
          <Steps>
            <li>
              <Trans i18nKey="In Le Chat, open <0>Intelligence › Connectors</0>, then <0>Add connector › Custom MCP connector</0>." components={[<Menu />]} />
            </li>
            <li>
              {t("Name it “ada” and paste the server URL:")}
              {url}
            </li>
            <li>
              <Trans i18nKey="Authentication method: <0>OAuth 2.1</0>. Click <0>Connect</0> and sign in with your Diametral Microsoft account." components={[<Menu />]} />
            </li>
          </Steps>
          <Callout type="info">{t("In a Mistral workspace, adding a connector may be reserved to its administrators.")}</Callout>
        </div>
      );
    case "vscode":
      return (
        <div className="ada-stack">
          <p className="ada-muted">{t("GitHub Copilot in agent mode. In the user or workspace configuration:")}</p>
          <Code filename="mcp.json" code={vscodeConfig(mcpUrl)} />
          <Steps>
            <li>
              <Trans i18nKey="Or run <0>MCP: Add Server…</0> from the command palette, choose <0>HTTP</0> and paste the server URL." components={[<Menu />]} />
            </li>
            <li>
              <Trans i18nKey="Start the server (<0>Start</0> above “ada” in mcp.json), accept the sign-in prompt and sign in with your Diametral Microsoft account." components={[<Menu />]} />
            </li>
          </Steps>
        </div>
      );
    case "gemini-cli":
      return (
        <div className="ada-stack">
          <Code filename="~/.gemini/settings.json" code={geminiConfig(mcpUrl)} />
          <Steps>
            <li>
              <Trans i18nKey="Then, in Gemini CLI: <0>/mcp auth ada</0>, and sign in with your Diametral Microsoft account." components={[<code className="ada-mono" />]} />
            </li>
          </Steps>
        </div>
      );
  }
}

const PROMPTS = [
  { name: "cra_depuis_commits", description: "Drafts your weekly report from your Git commits on the repositories linked to your projects." },
  {
    name: "revue_staffing",
    description: "DAR: weekly review of requests, people without an assignment and report compliance.",
  },
  { name: "synthese_projet", description: "Summary of a project's reports and team moves over the last weeks." },
];

export function IntegrationsPage() {
  const { t } = useTranslation();
  const mcpUrl = config.mcpUrl;
  const [params, setParams] = useSearchParams();
  const requested = params.get("client");
  const client: Client = CLIENTS.includes(requested as Client) ? (requested as Client) : "claude";
  const tabs: Array<{ id: Client; label: string }> = [
    { id: "claude", label: "Claude" },
    { id: "claude-code", label: "Claude Code" },
    { id: "chatgpt", label: "ChatGPT" },
    { id: "le-chat", label: "Le Chat" },
    { id: "vscode", label: "VS Code" },
    { id: "gemini-cli", label: "Gemini CLI" },
  ];

  return (
    <Page
      kicker={t("My space")}
      title={t("Integrations")}
      description={t(
        "ada exposes an MCP server: your AI assistant can read your projects, draft your weekly report from your commits and, for the DAR, prepare decisions. Every write goes through a preview and your confirmation.",
      )}
    >
      <Card>
        <CardHeader>
          <CardTitle>{t("Server URL")}</CardTitle>
          <CardDescription>
            {t("The only thing to paste. No client ID or secret: the assistant registers itself, then you sign in with Microsoft.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Snippet value={mcpUrl} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("Set up your assistant")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="ada-stack">
            <Tabs
              items={tabs}
              value={client}
              onChange={(id) => setParams(id === "claude" ? {} : { client: id }, { replace: true })}
            />
            <ClientGuide client={client} mcpUrl={mcpUrl} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("Available prompts")}</CardTitle>
          <CardDescription>
            {t("Type “/” in Claude (or your assistant's prompt menu) to find them. In Claude Code: /mcp__ada__<name>.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="ada-list">
            {PROMPTS.map((prompt) => (
              <li key={prompt.name} className="ada-stack ada-stack--tight">
                <code className="ada-mono">{prompt.name}</code>
                <span className="ada-muted">{t(prompt.description)}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Callout type="info" heading={t("Good to know")}>
        {t(
          "The assistant acts as you, with your rights in ada. To cut its access, remove the connector in the assistant; your Microsoft session stays untouched.",
        )}
      </Callout>
    </Page>
  );
}
